// Copyright (c) Microsoft. All rights reserved.

import { Plugin } from '../../redux/features/plugins/PluginsState';
import { ChatMemorySource } from '../models/ChatMemorySource';
import { IChatMessage } from '../models/ChatMessage';
import { IChatParticipant } from '../models/ChatParticipant';
import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { IChatSessionWithMessages } from '../models/ChatSessionWithMessages';
import { IChatUser } from '../models/ChatUser';
import { IGraphChatUser } from '../models/GraphChatUser';
import { ServiceInfo } from '../models/ServiceInfo';
import { IAsk, IAskVariables } from '../semantic-kernel/model/Ask';
import { IAskResult } from '../semantic-kernel/model/AskResult';
import { ICustomPlugin } from '../semantic-kernel/model/CustomPlugin';
import { BaseService } from './BaseService';

export class ChatService extends BaseService {
    public createChatAsync = async (
        title: string,
        accessToken: string,
        personaId?: string,
    ): Promise<ICreateChatSessionResponse> => {
        const body = {
            title,
            personaId,
        };

        const result = await this.getResponseAsync<ICreateChatSessionResponse>(
            {
                commandPath: 'chats',
                method: 'POST',
                body,
            },
            accessToken,
        );

        return result;
    };

    public chatMessageFeedbackAsync = async (
        chatId: string,
        messageId: string,
        feedback: boolean,
        accessToken: string,
    ): Promise<boolean> => {
        const body = {
            feedback,
        };

        const result = await this.getResponseAsync<boolean>(
            {
                commandPath: `chats/${chatId}/messages/${messageId}`,
                method: 'POST',
                body,
            },
            accessToken,
        );
        return result;
    };

    public getChatAsync = async (chatId: string, accessToken: string): Promise<IChatSession> => {
        const result = await this.getResponseAsync<IChatSession>(
            {
                commandPath: `chats/${chatId}`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public getAllChatsAsync = async (accessToken: string): Promise<IChatSession[]> => {
        const result = await this.getResponseAsync<IChatSession[]>(
            {
                commandPath: 'chats',
                method: 'GET',
            },
            accessToken,
        );
        return result;
    };

    public getAllChatsWithMessagesAsync = async (accessToken: string): Promise<IChatSessionWithMessages[]> => {
        const result = await this.getResponseAsync<IChatSessionWithMessages[]>(
            {
                commandPath: 'chats/messages',
                method: 'GET',
            },
            accessToken,
        );

        result.forEach((chat) => {
            chat.users = chat.participants.map<IChatUser>((participant) => ({
                id: participant.userId,
                online: false,
                fullName: participant.name,
                emailAddress: '', // The user's email address is not returned from the server
                isTyping: false,
                photo: '',
            }));
        });

        return result;
    };

    public getChatMessagesAsync = async (
        chatId: string,
        startIdx: number,
        count: number,
        accessToken: string,
    ): Promise<IChatMessage[]> => {
        const result = await this.getResponseAsync<IChatMessage[]>(
            {
                commandPath: `chats/${chatId}/messages?startIdx=${startIdx}&count=${count}`,
                method: 'GET',
            },
            accessToken,
        );

        // Messages are returned with most recent message at index 0 and oldest message at the last index,
        // so we need to reverse the order for render
        return result.reverse();
    };

    public editChatAsync = async (
        chatId: string,
        title: string,
        systemDescription: string,
        memoryBalance: number,
        accessToken: string,
        personaId?: string,
        personaName?: string,
    ): Promise<any> => {
        const body: IChatSession = {
            id: chatId,
            title,
            systemDescription,
            memoryBalance,
            personaId,
            personaName,
            chatOwner: '', // edit will not modify the chat owner
            enabledPlugins: [], // edit will not modify the enabled plugins
            setWebSearch: false,
        };

        const result = await this.getResponseAsync<IChatSession>(
            {
                commandPath: `chats/${chatId}`,
                method: 'PATCH',
                body,
            },
            accessToken,
        );

        return result;
    };

    public deleteChatAsync = async (chatId: string, accessToken: string): Promise<object> => {
        const result = await this.getResponseAsync<object>(
            {
                commandPath: `chats/${chatId}`,
                method: 'DELETE',
            },
            accessToken,
        );

        return result;
    };

    public getBotResponseAsync = async (
        ask: IAsk,
        accessToken: string,
        enabledPlugins?: Plugin[],
        processPlan = false,
    ): Promise<IAskResult> => {
        // If function requires any additional api properties, append to context
        if (enabledPlugins && enabledPlugins.length > 0) {
            const openApiVariables: IAskVariables[] = [];

            // List of custom plugins to append to context variables
            const customPlugins: ICustomPlugin[] = [];

            for (const plugin of enabledPlugins) {
                // If user imported a manifest domain, add custom plugin
                if (plugin.manifestDomain) {
                    customPlugins.push({
                        nameForHuman: plugin.name,
                        nameForModel: plugin.nameForModel as string,
                        authHeaderTag: plugin.headerTag,
                        authType: plugin.authRequirements.personalAccessToken ? 'user_http' : 'none',
                        manifestDomain: plugin.manifestDomain,
                    });
                }

                // If functions requires any additional api properties, append to context variables
                if (plugin.apiProperties) {
                    const apiProperties = plugin.apiProperties;

                    for (const property in apiProperties) {
                        const propertyDetails = apiProperties[property];

                        if (propertyDetails.required && !propertyDetails.value) {
                            throw new Error(`Missing required property ${property} for ${plugin.name} plugin.`);
                        }

                        if (propertyDetails.value) {
                            openApiVariables.push({
                                key: property,
                                value: propertyDetails.value,
                            });
                        }
                    }
                }
            }

            if (customPlugins.length > 0) {
                openApiVariables.push({
                    key: `customPlugins`,
                    value: JSON.stringify(customPlugins),
                });
            }

            ask.variables = ask.variables ? ask.variables.concat(openApiVariables) : openApiVariables;
        }

        const chatId = ask.variables?.find((variable) => variable.key === 'chatId')?.value as string;

        const result = await this.getResponseAsync<IAskResult>(
            {
                commandPath: `chats/${chatId}/${processPlan ? 'plan' : 'messages'}`,
                method: 'POST',
                body: ask,
            },
            accessToken,
            enabledPlugins,
        );

        return result;
    };

    public joinChatAsync = async (chatId: string, accessToken: string): Promise<IChatSession> => {
        await this.getResponseAsync<any>(
            {
                commandPath: `chats/${chatId}/participants`,
                method: 'POST',
            },
            accessToken,
        );

        return await this.getChatAsync(chatId, accessToken);
    };

    public addUserToChatAsync = async (chatId: string, userId: string, accessToken: string): Promise<IChatSession> => {
        await this.getResponseAsync<any>(
            {
                commandPath: `chats/${chatId}/participants/${userId}`,
                method: 'POST',
            },
            accessToken,
        );

        return await this.getChatAsync(chatId, accessToken);
    };

    public getChatMemorySourcesAsync = async (chatId: string, accessToken: string): Promise<ChatMemorySource[]> => {
        const result = await this.getResponseAsync<ChatMemorySource[]>(
            {
                commandPath: `chats/${chatId}/documents`,
                method: 'GET',
            },
            accessToken,
        );
        return result;
    };

    public getAllChatParticipantsAsync = async (chatId: string, accessToken: string): Promise<IChatUser[]> => {
        const result = await this.getResponseAsync<IChatParticipant[]>(
            {
                commandPath: `chats/${chatId}/participants`,
                method: 'GET',
            },
            accessToken,
        );

        const chatUsers = result.map<IChatUser>((participant) => ({
            id: participant.userId,
            online: false,
            fullName: participant.name,
            emailAddress: '', // The user's email address is not returned from the server
            isTyping: false,
            photo: '',
        }));

        return chatUsers;
    };

    public deleteChatParticipantAsync = async (chatId: string, userId: string, accessToken: string): Promise<any> => {
        await this.getResponseAsync<any>(
            {
                commandPath: `chats/${chatId}/participants/${userId}`,
                method: 'DELETE',
            },
            accessToken,
        );
    };

    public transferChatOwnershipAsync = async (
        chatId: string,
        newOwnerId: string,
        accessToken: string,
    ): Promise<IChatSession> => {
        return await this.getResponseAsync<IChatSession>(
            {
                commandPath: `chats/${chatId}/transferOwnership/${newOwnerId}`,
                method: 'PATCH',
            },
            accessToken,
        );
    };

    public getSemanticMemoriesAsync = async (
        chatId: string,
        memoryName: string,
        accessToken: string,
    ): Promise<string[]> => {
        const result = await this.getResponseAsync<string[]>(
            {
                commandPath: `chats/${chatId}/memories?type=${memoryName}`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public generateChatTitleAsync = async (chatId: string, accessToken: string): Promise<IChatSession> => {
        const result = await this.getResponseAsync<IChatSession>(
            {
                commandPath: `chats/${chatId}/title`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public getServiceInfoAsync = async (accessToken: string): Promise<ServiceInfo> => {
        const result = await this.getResponseAsync<ServiceInfo>(
            {
                commandPath: `info`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public getDocumentBytesAsync = async (chatId: string, documentId: string, accessToken: string): Promise<string> => {
        const result = await this.getResponseAsync<string>(
            {
                commandPath: `documents/bytearray/${documentId}/${chatId}/CONVERSION`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public getDocumentBytesCitationsAsync = async (
        chatId: string,
        documentId: string,
        messageId: string,
        accessToken: string,
    ): Promise<string> => {
        const result = await this.getResponseAsync<string>(
            {
                commandPath: `documents/bytearray/${documentId}/${chatId}/${messageId}/CITATIONS`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };

    public searchUsersAsync = async (searchTerm: string, accessToken: string): Promise<IGraphChatUser[]> => {
        const result = await this.getResponseAsync<IGraphChatUser[]>(
            {
                commandPath: `users/search?searchTerm=${searchTerm}`,
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };
}
